import * as React from "react";
import { useParams } from "react-router-dom";
import { ResizableBox } from "react-resizable";
import {
    RangeSlider,
    ChunkContainer,
    Spinner,
    ScrubBar,
    Button,
    VideoPlayer,
    SaveFragmentModal,
    EditorTabs,
    EditorNavigation,
    PlayerButtons,
} from "ui/components";
import { useEditorContext } from "ui/context/EditorContext";
import createHLSManifestObject, {
    createHLSManifestObjectFromSelections,
} from "utils/createHLSManifestObject";
import getWindowHeightOnPercentage from "utils/getWindowHeightOnPercentage";
import {
    Channel,
    Selection,
    PlayerButtonType,
    VideoActionRequest,
    segmentTimeDisplayString,
} from "core/models";
import { getChannel } from "core/services";
import { useSessionContext } from "ui/context/SessionContext";
import { createMediaFile, fetchMediaFile } from "core/services/site/mediafile";
import { SelectionRequest } from "core/services/site/fragment";
import createPoller from "utils/createPoller";
import styles from "./EditorPageStyles.module.scss";

const MULTIPLIER = 1000;
const VIDEO_FPS = 25;
const RESIZE_PREFERENCE_KEY = "media.spot-on.editorChunkContainerSize";

function tsLengthToRangeValue(tsLength: number) {
    return tsLength * MULTIPLIER;
}
function rangeValueToTsLength(rangeValue: number) {
    return rangeValue / MULTIPLIER;
}

const alwaysDouble = (n: number) => (n < 10 ? `0${n}` : n.toString());
function formatDuration(durationInSeconds: number) {
    const roundedDuration = Math.ceil(durationInSeconds);
    const minutes = Math.floor(roundedDuration / 60);
    const seconds = roundedDuration - minutes * 60;
    return `${alwaysDouble(minutes)}:${alwaysDouble(seconds)}`;
}

const Editor: React.FC = () => {
    const params = useParams();
    const { channelID } = params;

    if (!channelID) {
        throw new Error("Channel ID is missing");
    }

    const editorContext = useEditorContext();
    const sessionContext = useSessionContext();

    const defaultWidth = window.innerWidth - 90;
    const defaultHeight = getWindowHeightOnPercentage(40);
    const [resizable, setResizable] = React.useState<{
        height: number;
        width: number;
        minConstraints: [number, number];
        maxConstraints: [number, number];
    }>({
        height: localStorage.getItem(RESIZE_PREFERENCE_KEY)
            ? parseInt(localStorage.getItem(RESIZE_PREFERENCE_KEY) || "0", 10)
            : defaultHeight,
        width: window.innerWidth - 90,
        minConstraints: [defaultWidth, getWindowHeightOnPercentage(20)],
        maxConstraints: [defaultWidth, window.innerHeight - 15],
    });
    const [channel, setChannel] = React.useState<Channel>();
    const [saveSpot, setSaveSpot] = React.useState<boolean>(false);
    const [videoURL, setVideoURL] = React.useState<string | null>(null);
    const [videoType, setVideoType] = React.useState<"stream" | "video">(
        "stream"
    );
    const [selectedTrimBar, setSelectedTrimBar] = React.useState<number>(-1);
    const [previewPlayerTimestamp, setPreviewPlayerTimestamp] =
        React.useState<number>(0);
    const [preparingTrim, setPreparingTrim] = React.useState<boolean>(false);
    const [playing, setPlaying] = React.useState<boolean>(false);
    const [screenshot, setScreenshot] = React.useState<string | null>(null);
    const [useScreenshot, setUseScreenshot] = React.useState<boolean>(false);
    const [autoplay, setAutoplay] = React.useState<boolean | "end">(false);
    const [videoPlayerRequest, setVideoPlayerRequest] =
        React.useState<VideoActionRequest>({ type: "none" });
    const [selectionTimestamp, setSelectionTimestamp] =
        React.useState<string>();

    const containerRef = React.createRef<HTMLDivElement>();

    const currentSelection: Selection =
        editorContext.selections.find(
            (x) => x.id === editorContext.currentSelectionID
        ) || editorContext.selections[0];

    React.useEffect(() => {
        if (currentSelection.segments.length > 0) {
            const selectionTimestampString = segmentTimeDisplayString(
                currentSelection.segments[0]
            );
            setSelectionTimestamp(selectionTimestampString);
        }
    }, [currentSelection]);

    React.useEffect(() => {
        const load = async () => {
            if (!sessionContext.session) {
                return;
            }
            const result = await getChannel(
                sessionContext.session.token,
                parseInt(channelID, 10)
            );
            if (!result.value) {
                return;
            }
            setChannel(result.value);
            editorContext.setChannelId(parseInt(channelID, 10));
        };
        load();
    }, [channelID, editorContext, sessionContext]);

    React.useEffect(() => {
        const { selections } = editorContext;
        if (selections.length === 0) {
            setVideoURL(null);
            return undefined;
        }
        if (!currentSelection || currentSelection.segments.length === 0) {
            setVideoURL(null);
            return undefined;
        }

        if (
            currentSelection.mediaFile &&
            currentSelection.mediaFile.isProcessing === false
        ) {
            setVideoType("video");
            setVideoURL(currentSelection.mediaFile.getMediaURL());
            return undefined;
        }

        const segmentsReversed = [...currentSelection.segments].reverse();
        const manifest = createHLSManifestObject(
            parseInt(channelID, 10),
            segmentsReversed
        );
        const newVideoURL = manifest ? URL.createObjectURL(manifest) : null;

        if (videoURL) {
            URL.revokeObjectURL(videoURL);
        }
        setVideoType("stream");
        setVideoURL(newVideoURL);

        return () => {
            if (videoURL) {
                URL.revokeObjectURL(videoURL);
            }
        };
    }, [
        editorContext.selections,
        editorContext.currentSelectionID,
        channelID,
        currentSelection,
    ]);

    const onEnableTrim = async (selection: Selection) => {
        setAutoplay(false);
        setPreparingTrim(true);
        const data: SelectionRequest = {
            trimStart: selection.trimStart,
            trimEnd: selection.trimEnd,
            firstSegmentNumber:
                selection.segments[selection.segments.length - 1].number,
            lastSegmentNumber: selection.segments[0].number,
            segmentCount: selection.segments.length,
        };
        if (!sessionContext.session || !sessionContext.selectedProject) {
            return;
        }
        const mediaFileResponse = await createMediaFile(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
            parseInt(channelID, 10),
            data
        );
        const mediaFile = mediaFileResponse.value;
        if (!mediaFile) {
            return;
        }
        const currentSelectionIdx = editorContext.selections.findIndex(
            (x) => x.id === selection.id
        );
        const newSelections = [...editorContext.selections];
        newSelections[currentSelectionIdx].mediaFile = mediaFile;
        editorContext.setSelections(newSelections);

        createPoller(async () => {
            if (!sessionContext.session) {
                return true;
            }
            const pollResult = await fetchMediaFile(
                sessionContext.session.token,
                mediaFile.id
            );
            if (!pollResult.value) {
                return true;
            }
            if (pollResult.value.isProcessing) {
                return false;
            }
            const selectionIdx = editorContext.selections.findIndex(
                (x) => x.id === selection.id
            );
            const newerSelections = [...editorContext.selections];
            newSelections[selectionIdx].mediaFile = pollResult.value;
            editorContext.setSelections(newerSelections);
            setPreparingTrim(false);
            return true;
        }, [{ interval: 1 * 1000, iterations: 120 }]);
    };

    const handleResize = (event: any, data: any) => {
        localStorage.setItem(RESIZE_PREFERENCE_KEY, `${data.size.height}`);
        setResizable({
            ...resizable,
            height: data.size.height,
            width: data.size.width,
        });
    };

    const togglePlay = React.useCallback(() => {
        setVideoPlayerRequest({ type: "togglePlay" });
        editorContext.setPreviewing(false);
    }, [editorContext]);
    const rewind = React.useCallback(() => {
        setVideoPlayerRequest({ type: "rewind" });
    }, []);
    const moveFrameBackward = React.useCallback(() => {
        setVideoPlayerRequest({ type: "frameBackwards" });
    }, []);
    const moveFrameForward = React.useCallback(() => {
        setVideoPlayerRequest({ type: "frameForwards" });
    }, []);
    const playLastSeconds = (seconds: number) => {
        setVideoPlayerRequest({ type: "playLastSeconds", value: seconds });
    };
    const playFromStart = React.useCallback(() => {
        rewind();
    }, [rewind]);

    const goMarkOut = React.useCallback(() => {
        playLastSeconds(0.04);
    }, []);
    const goToInPoint = React.useCallback(() => {
        setVideoPlayerRequest({
            type: "setTime",
            value: currentSelection.trimStart,
        });
    }, [currentSelection.trimStart]);

    const playerButtonClicked = (buttonType: PlayerButtonType): void => {
        switch (buttonType) {
            case "goToInPoint":
                goToInPoint();
                setSelectedTrimBar(0);
                break;
            case "goToOutPoint":
                goMarkOut();
                setSelectedTrimBar(1);
                break;
            case "moveFrameBackward":
                moveFrameBackward();
                break;
            case "moveFrameForward":
                moveFrameForward();
                break;
            case "playFromStart":
                playFromStart();
                break;
            case "playLast2Seconds":
                playLastSeconds(2);
                break;
            case "togglePlay":
                togglePlay();
                break;
            default:
                break;
        }
    };

    function setResizableBoxDimensions() {
        const { innerWidth } = window;
        const resizableHeight = parseInt(
            localStorage.getItem(RESIZE_PREFERENCE_KEY) || "0",
            10
        );
        const defaultResizableBoxWidth = innerWidth - 72;
        const newResizable = {
            height: resizableHeight || getWindowHeightOnPercentage(50),
            width: window.innerWidth - 90,
            minConstraints: [
                defaultResizableBoxWidth,
                getWindowHeightOnPercentage(20),
            ] as [number, number],
            maxConstraints: [
                defaultResizableBoxWidth,
                window.innerHeight - 15,
            ] as [number, number],
        };
        setResizable(newResizable);
    }

    React.useEffect(() => {
        if (!saveSpot) {
            window.addEventListener("resize", setResizableBoxDimensions);
        }
        return () => {
            if (!saveSpot) {
                window.removeEventListener("resize", setResizableBoxDimensions);
            }
        };
    }, [saveSpot]);

    React.useEffect(() => {
        setResizableBoxDimensions();
    }, []);

    const handleTimeUpdate = (time: number) => {
        setPreviewPlayerTimestamp(time);
    };

    const handleSaveClip = React.useCallback(() => {
        setSaveSpot(true);
    }, []);

    const handleVideoPlayState = (status: boolean) => {
        setPlaying((prevPlaying) => {
            if (prevPlaying !== status) {
                return status;
            }
            return prevPlaying;
        });
    };

    const handleNewScreenshot = (newScreenshot: string) => {
        setScreenshot(newScreenshot);
        if (!useScreenshot) {
            return;
        }
        setUseScreenshot(false);
        editorContext.setThumbnailURL(newScreenshot);
    };
    const handleScreenshotRequest = (): string | null => {
        setVideoPlayerRequest({ type: "getScreenshot" });
        setUseScreenshot(true);
        return screenshot;
    };

    const onVideoEnded = (): void => {
        editorContext.setPreviewing(false);
    };

    const onPreview = async () => {
        if (editorContext.selections.length === 0) {
            editorContext.setPreviewing(false);
            return;
        }
        editorContext.setPreviewing(true);
        const manifest = createHLSManifestObjectFromSelections(
            parseInt(channelID, 10),
            [...editorContext.selections]
        );
        const newVideoURL = manifest ? URL.createObjectURL(manifest) : null;
        setVideoURL(newVideoURL);
    };

    const renderVideoOverlay = (videoState: any): React.ReactNode => {
        if (!videoState) {
            return null;
        }
        const newCurrentSelection: Selection =
            editorContext.selections.find(
                (x) => x.id === editorContext.currentSelectionID
            ) || editorContext.selections[0];
        let firstChunk = null;
        let lastChunk = null;
        let isSingleChunk = false;
        let beginDuration = 0;
        let endDuration = 0;

        const onChangeTrimStart = (tsLength: number) => {
            if (!currentSelection) {
                return;
            }
            const firstChunkLength =
                currentSelection.segments[currentSelection.segments.length - 1]
                    .tsLength;
            let newTsLength = tsLength;
            if (newTsLength < 0) {
                newTsLength = 0;
            }
            if (newTsLength > firstChunkLength) {
                newTsLength = firstChunkLength;
            }
            const { selections, currentSelectionID } = editorContext;
            const currentSelectionIdx = selections.findIndex(
                (x) => x.id === currentSelectionID
            );
            const newSelections = [...selections];
            newSelections[currentSelectionIdx].trimStart = newTsLength;
            setSelectedTrimBar(0);
            editorContext.setSelections(newSelections);
            videoState.setCurrentTime(currentSelection.trimStart);
        };
        const onChangeTrimEnd = (tsLength: number) => {
            if (!currentSelection) {
                return;
            }
            const lastChunkLength = currentSelection.segments[0].tsLength;
            let newTsLength = tsLength;
            if (newTsLength < 0) {
                newTsLength = 0;
            }
            if (newTsLength > lastChunkLength) {
                newTsLength = lastChunkLength;
            }
            const { selections, currentSelectionID } = editorContext;
            const currentSelectionIdx = selections.findIndex(
                (x) => x.id === currentSelectionID
            );
            const newSelections = [...selections];
            newSelections[currentSelectionIdx].trimEnd = newTsLength;
            setSelectedTrimBar(1);
            editorContext.setSelections(newSelections);
            videoState.setCurrentTime(
                videoState.duration - currentSelection.trimEnd
            );
        };

        if (newCurrentSelection && newCurrentSelection.segments.length > 0) {
            firstChunk =
                newCurrentSelection.segments[
                    newCurrentSelection.segments.length - 1
                ];
            [lastChunk] = newCurrentSelection.segments;
            beginDuration = firstChunk.tsLength;
            endDuration = lastChunk.tsLength;
            isSingleChunk =
                newCurrentSelection.startSegmentID ===
                newCurrentSelection.endSegmentID;

            if (isSingleChunk) {
                beginDuration /= 2;
                endDuration /= 2;
            }
        }
        const trimBeginSelected = selectedTrimBar === 0;
        const trimEndSelected = selectedTrimBar === 1;

        return (
            <div className={styles.videooverlay}>
                <div className={styles.videooverlay__inner}>
                    {videoState.buffering && !preparingTrim && (
                        <div className={styles.videooverlay__buffering}>
                            <Spinner
                                className={
                                    styles.videooverlay__buffering__spinner
                                }
                            />
                            <span>Loading…</span>
                        </div>
                    )}
                    {preparingTrim && (
                        <div className={styles.videooverlay__buffering}>
                            <Spinner
                                className={
                                    styles.videooverlay__buffering__spinner
                                }
                            />
                            <span>Preparing trim options…</span>
                        </div>
                    )}
                    {!preparingTrim && (
                        <div className={styles.videooverlay__handles}>
                            <div className={styles.scrubbar}>
                                {videoState.vidRef && (
                                    <ScrubBar
                                        vidRef={videoState.vidRef}
                                        trim={[
                                            currentSelection.trimStart,
                                            currentSelection.trimEnd,
                                        ]}
                                        selectionTimestamp={selectionTimestamp}
                                        inEditor
                                    />
                                )}
                            </div>
                            <span className={styles.duration}>
                                {formatDuration(videoState.duration)}
                            </span>
                        </div>
                    )}
                </div>
                {currentSelection && videoType === "video" && (
                    <div className={styles.trimmer}>
                        <div className={styles.trim__buttons__container}>
                            <Button
                                type="function"
                                theme="light"
                                icon="frame-backward"
                                tooltip="1 Frame left"
                                onClick={() => {
                                    onChangeTrimStart(
                                        currentSelection.trimStart - 0.04
                                    );
                                }}
                                disabled={currentSelection.trimStart <= 0}
                            />
                            <Button
                                type="function"
                                theme="light"
                                icon="frame-forward"
                                tooltip="1 Frame right"
                                onClick={() => {
                                    onChangeTrimStart(
                                        currentSelection.trimStart + 0.04
                                    );
                                }}
                                disabled={
                                    currentSelection.trimStart ===
                                    firstChunk?.tsLength
                                }
                            />
                        </div>
                        <div className={styles.slider__container}>
                            <RangeSlider
                                className={styles.trim__slider}
                                enabled={trimBeginSelected}
                                maxValue={
                                    firstChunk
                                        ? tsLengthToRangeValue(beginDuration)
                                        : undefined
                                }
                                value={tsLengthToRangeValue(
                                    currentSelection.trimStart
                                )}
                                onChange={(value) => {
                                    onChangeTrimStart(
                                        rangeValueToTsLength(value)
                                    );
                                }}
                                onDraggingEnd={() => {
                                    videoState.setCurrentTime(
                                        currentSelection.trimStart
                                    );
                                    videoState.play();
                                }}
                            />
                            <div className={styles.slider__label}>
                                First segment
                            </div>
                        </div>
                        <div className={styles.divide} />
                        <div className={styles.slider__container}>
                            <RangeSlider
                                className={styles.trim__slider}
                                enabled={trimEndSelected}
                                maxValue={
                                    lastChunk
                                        ? tsLengthToRangeValue(endDuration)
                                        : undefined
                                }
                                value={tsLengthToRangeValue(
                                    currentSelection.trimEnd
                                )}
                                onChange={(value) =>
                                    onChangeTrimEnd(rangeValueToTsLength(value))
                                }
                                onDraggingEnd={() => {
                                    videoState.setCurrentTime(
                                        videoState.duration -
                                            currentSelection.trimEnd -
                                            1
                                    );
                                    videoState.play();
                                }}
                                inverse
                            />
                            <div className={styles.slider__label}>
                                Last segment
                            </div>
                        </div>
                        <div className={styles.trim__buttons__container}>
                            <Button
                                type="function"
                                theme="light"
                                icon="frame-backward"
                                tooltip="1 Frame left"
                                disabled={
                                    currentSelection.trimEnd ===
                                    lastChunk?.tsLength
                                }
                                onClick={() => {
                                    onChangeTrimEnd(
                                        currentSelection.trimEnd + 1 / VIDEO_FPS
                                    );
                                }}
                            />
                            <Button
                                type="function"
                                theme="light"
                                icon="frame-forward"
                                tooltip="1 Frame right"
                                disabled={currentSelection.trimEnd <= 0}
                                onClick={() => {
                                    onChangeTrimEnd(
                                        currentSelection.trimEnd - 1 / VIDEO_FPS
                                    );
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };
    return (
        <div className={styles.container} ref={containerRef}>
            <ResizableBox
                className={styles.resizable}
                height={resizable.height}
                width={resizable.width}
                minConstraints={resizable.minConstraints}
                maxConstraints={resizable.maxConstraints}
                onResize={handleResize}
                axis="y"
            >
                <div className={styles.videosection}>
                    <div className={styles.playersection}>
                        {videoURL ? (
                            <div className={styles.videocontainer}>
                                <div className={styles.video}>
                                    <VideoPlayer
                                        type={videoType}
                                        videoSrc={videoURL}
                                        onTimeUpdate={handleTimeUpdate}
                                        overlay={renderVideoOverlay}
                                        controls={false}
                                        trim={[
                                            currentSelection.trimStart || 0,
                                            currentSelection.trimEnd || 0,
                                        ]}
                                        actionRequest={videoPlayerRequest}
                                        onPlaying={handleVideoPlayState}
                                        autoplay={autoplay}
                                        onScreenshot={handleNewScreenshot}
                                        onEnded={onVideoEnded}
                                        verticalResize
                                    />
                                </div>
                                <PlayerButtons
                                    onButtonClick={playerButtonClicked}
                                    playing={playing}
                                    useShortcuts={!saveSpot}
                                />
                            </div>
                        ) : (
                            <div className={styles.placeholder}>
                                <span className={styles.placeholder__message}>
                                    Start by selecting segments below
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={styles.videosection__spacer} />
                    <div className={styles.infosection}>
                        <EditorTabs
                            requestScreenshot={handleScreenshotRequest}
                            onEnableTrim={onEnableTrim}
                            onPreview={onPreview}
                        />
                    </div>
                </div>
            </ResizableBox>
            {channel && (
                <>
                    <EditorNavigation
                        onSaveSelections={handleSaveClip}
                        channel={channel}
                    />
                    <ChunkContainer
                        height={resizable.height}
                        channelID={parseInt(channelID, 10)}
                        className={styles.chunk__section}
                        previewPlayerTime={previewPlayerTimestamp}
                        savingClip={saveSpot}
                        channel={channel}
                        onAutoplay={setAutoplay}
                    />
                </>
            )}

            {saveSpot && channel && (
                <SaveFragmentModal
                    channel={channel}
                    onClose={() => setSaveSpot(false)}
                />
            )}
        </div>
    );
};

export default Editor;
